import React, { useEffect, useRef, useState } from "react"; // useEffect used for addressant sync
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";

import { FundingCampaignSubmissionDetail } from "../../../services/fundingCampaign";

interface Props {
  data: FundingCampaignSubmissionDetail;
  visible: boolean;
  onHide: () => void;
  onCopied: () => void;
}

const QUESTION_LABELS: Record<string, string> = {
  owns_property:           "Ist der Antragsteller Eigentümer der Immobilie?",
  building_type_eligible:  "Gehört das Gebäude zur förderberechtigten Kategorie?",
  no_geig_obligation:      "Bestandsgebäude ohne GEIG-Pflicht?",
  has_parking:             "Sind ausreichend Stellplätze vorhanden?",
  parking_is_private:      "Werden die Stellplätze wohnbezogen genutzt?",
  pre_wiring_scope:        "Ist der Mindestumfang der Vorverkabelung umsetzbar?",
  no_contracts:            "Wurde noch kein Projektstart durchgeführt?",
  no_exclusion_conditions: "Liegen keine Ausschlusskriterien vor?",
  renewable_energy:        "Können erneuerbare Energien genutzt werden?",
};

const esc = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const translateAnswer = (v: string): string => {
  if (v === "yes") return "Ja";
  if (v === "no") return "Nein";
  return v;
};

function buildSubject(d: FundingCampaignSubmissionDetail): string {
  return `Angebotsanfrage Wallbox-Installation – ${d.first_name} ${d.last_name}`;
}

function buildGreeting(addressant: string): string {
  const trimmed = addressant.trim();
  if (!trimmed) return "Sehr geehrte Damen und Herren,";
  const lower = trimmed.toLowerCase();
  const salutation = lower.startsWith("herr")
    ? "geehrter"
    : lower.startsWith("frau")
    ? "geehrte"
    : "geehrte/r";
  return `Sehr ${salutation} ${trimmed},`;
}

function buildBodyHtml(d: FundingCampaignSubmissionDetail, addressant: string): string {
  const address = [d.street, [d.postal_code, d.city].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");

  const answersBlock = d.answers
    ? Object.keys(d.answers)
        .map((key) => {
          const question = esc(QUESTION_LABELS[key] ?? key);
          const answer = esc(translateAnswer((d.answers as Record<string, string>)[key]));
          return `\n\n${question}: <strong>${answer}</strong>`;
        })
        .join("")
    : "";

  const hasQuestionnaire = d.answers && Object.keys(d.answers).length > 0;

  return `<span id="email-greeting">${esc(buildGreeting(addressant))}</span>

wir freuen uns, Ihnen eine neue Anfrage im Rahmen unseres Wallbox-Förderprogramms zu übermitteln. Der folgende Antragsteller hat Interesse an einer Wallbox-Installation angemeldet und bittet um ein Angebot.

─────────────────────────────────
PERSÖNLICHE DATEN
─────────────────────────────────
Name:          ${esc(`${d.first_name} ${d.last_name}`)}
${d.company ? `Unternehmen:   ${esc(d.company)}\n` : ""}
─────────────────────────────────
KONTAKTDATEN
─────────────────────────────────
${d.phone ? `Telefon:       ${esc(d.phone)}\n` : ""}E-Mail:        ${esc(d.email)}
${d.cc_emails && d.cc_emails.length > 0 ? `CC:            ${esc(d.cc_emails.join(", "))}\n` : ""}
─────────────────────────────────
IMMOBILIENDETAILS
─────────────────────────────────
${address ? `Adresse:       ${esc(address)}\n` : ""}Stellplätze:   ${d.parking_spaces != null ? d.parking_spaces : "-"}

${hasQuestionnaire ? `─────────────────────────────────
FRAGEBOGEN
─────────────────────────────────${answersBlock}
` : ""}─────────────────────────────────

Für Rückfragen stehen wir Ihnen jederzeit gerne zur Verfügung.

Mit freundlichen Grüßen,
cocharge Team`;
}

const EmailTemplateDialog: React.FC<Props> = ({ data, visible, onHide, onCopied }) => {
  const [addressant, setAddressant] = useState("");
  const [editableSubject, setEditableSubject] = useState(() => buildSubject(data));
  const bodyRef = useRef<HTMLDivElement>(null);

  const getBodyText = () => bodyRef.current?.innerText ?? "";

  // Called by Dialog's onShow — ref is guaranteed to be set at this point
  const handleShow = () => {
    setAddressant("");
    setEditableSubject(buildSubject(data));
    if (bodyRef.current) {
      bodyRef.current.innerHTML = buildBodyHtml(data, "");
    }
  };

  // Update only the greeting node when addressant changes, preserving user edits to the rest
  useEffect(() => {
    if (!bodyRef.current) return;
    const greetingEl = bodyRef.current.querySelector<HTMLElement>("#email-greeting");
    if (greetingEl) {
      greetingEl.textContent = buildGreeting(addressant);
    }
  }, [addressant]); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(onCopied);
  };

  return (
    <Dialog
      header="E-Mail-Vorlage"
      visible={visible}
      onHide={onHide}
      onShow={handleShow}
      style={{ width: "44rem" }}
    >

      <div className="flex flex-column gap-1 mb-4">
        <label className="text-xs font-semibold text-600 uppercase" style={{ letterSpacing: "0.07em" }}>
          Empfänger
        </label>
        <InputText
          value={addressant}
          onChange={(e) => setAddressant(e.target.value)}
          placeholder="z. B. Herr Müller / Elektro Müller GmbH"
          className="w-full"
        />
        <span className="text-xs text-400">
          Leer lassen für „Sehr geehrte Damen und Herren"
        </span>
      </div>

      <div className="flex flex-column gap-1 mb-3">
        <div className="flex align-items-center justify-content-between">
          <label className="text-xs font-semibold text-600 uppercase" style={{ letterSpacing: "0.07em" }}>
            Betreff
          </label>
          <Button
            label="Kopieren"
            icon="pi pi-copy"
            className="p-button-text p-button-sm text-xs"
            tooltip="Betreff kopieren"
            tooltipOptions={{ position: "top" }}
            onClick={() => copy(editableSubject)}
          />
        </div>
        <InputText
          value={editableSubject}
          onChange={(e) => setEditableSubject(e.target.value)}
          className="font-mono text-sm w-full"
        />
      </div>

      <div className="flex flex-column gap-1">
        <div className="flex align-items-center justify-content-between">
          <label className="text-xs font-semibold text-600 uppercase" style={{ letterSpacing: "0.07em" }}>
            E-Mail-Text
          </label>
          <Button
            label="Kopieren"
            icon="pi pi-copy"
            className="p-button-text p-button-sm text-xs"
            tooltip="Text kopieren"
            tooltipOptions={{ position: "top" }}
            onClick={() => copy(getBodyText())}
          />
        </div>
        <div
          ref={bodyRef}
          contentEditable
          suppressContentEditableWarning
          className="font-mono text-sm p-inputtext w-full"
          style={{ whiteSpace: "pre-wrap", minHeight: "22rem", overflowY: "auto", outline: "none" }}
        />
      </div>
    </Dialog>
  );
};

export default EmailTemplateDialog;
